import React from 'react';
import { Typography, Divider } from 'antd';
import './styles.scss';

const { Text } = Typography;

// ─── Label maps ──────────────────────────────────────────────────────────────

const sumTypeLabels = {
    none: 'None',
    sumperiods: 'Sum of periods',
    lastperiod: 'Last period',
    weightedavgnumber: 'Weighted avg (number)',
    weightedavgaccount: 'Weighted avg (account)',
};

const bcCombineLabels = {
    sum: 'Sum of scenarios',
    weighted_avg: 'Weighted average',
    derive: 'Calculate formula at BC level',
};

const rollUpLabels = {
    '': 'Additive',
    S: 'Subtractive',
    I: 'Ignore',
};

const financialGroupLabels = {
    1: 'Profit & Loss',
    2: 'Balance Sheet — Assets',
    3: 'Balance Sheet — Liabilities',
    4: 'Balance Sheet — Equity',
    5: 'Cash Flow',
    6: 'Drivers',
};

// ─── Formatters ──────────────────────────────────────────────────────────────

const formatSumType = (acc) => {
    const base = sumTypeLabels[acc.sum_type] || acc.sum_type || 'Sum of periods';
    if (
        (acc.sum_type === 'weightedavgnumber' || acc.sum_type === 'weightedavgaccount') &&
        acc.weight
    ) {
        return `${base} by ${acc.weight}`;
    }
    return base;
};

const formatBcCombine = (acc) => {
    const mode = acc.bc_combine || 'sum';
    const base = bcCombineLabels[mode] || bcCombineLabels.sum;
    if (mode === 'weighted_avg' && acc.bc_weight) {
        return `${base} by ${acc.bc_weight}`;
    }
    return base;
};

const formatMetric = (acc) => {
    if (acc.ratio) return 'Ratio';
    if (acc.unit) return 'Unit';
    if (acc.numeric) return 'Amount';
    return '—';
};

const formatRollUp = (acc) => rollUpLabels[acc.roll_up || ''] || 'Additive';

const formatYesNo = (value) => (value ? 'Yes' : 'No');

const countNonEmptyFormulas = (formulas) => {
    if (!formulas) return 0;
    return formulas.filter((f) => f.formula && f.formula.trim() !== '').length;
};

const formatCashEffect = (acc) => {
    if (acc.cash_effect === true) return 'Cash effect';
    if (acc.cash_effect === false) return 'Non-cash effect';
    return '—';
};

// ─── Default detectors ───────────────────────────────────────────────────────
// Each tells the renderer whether the value matches the system default,
// so we can mute it visually while still showing it.

const isDefault = {
    metric: (acc) => acc.numeric === true && !acc.ratio && !acc.unit,
    sumType: (acc) => !acc.sum_type || acc.sum_type === 'sumperiods',
    bcCombine: (acc) => (!acc.bc_combine || acc.bc_combine === 'sum'),
    rollUp: (acc) => !acc.roll_up || acc.roll_up === '',
    carry: (acc) => !acc.carry,
    convergence: (acc) => !acc.convergence,
    exclude: (acc) => !acc.exclude,
};

// ─── Subcomponents ───────────────────────────────────────────────────────────

const Row = ({ label, value, muted = false }) => (
    <div className="acc-summary-row">
        <Text type="secondary" className="acc-summary-label">
            {label}
        </Text>
        <Text
            className="acc-summary-value"
            style={{ color: muted ? '#999' : '#000' }}
        >
            {value}
        </Text>
    </div>
);

const SectionHeader = ({ children }) => (
    <Text type="secondary" className="acc-summary-section">
        {children}
    </Text>
);

// ─── Main component ──────────────────────────────────────────────────────────

const AccountConfigSummary = ({ account }) => {
    if (!account) return null;

    // Some rows wrap the actual account in `account.account` (custom tab accounts).
    const acc = account.account ? account.account : account;
console.log(acc);
console.log(acc.associated_account);
    const formulaCount = countNonEmptyFormulas(acc.formulas);
    const totalPeriods = acc.formulas?.length || 0;
    const financialGroup = financialGroupLabels[acc.financial_group_type] || '—';

    const cashEffect =
    acc.cash_effect ??
    acc.associated_account?.[0]?.cash_effect;

    return (
        <div className="acc-summary">
            {/* Identity */}
            <div className="acc-summary-header">
                <Text strong>{acc.account_number}</Text>
                {acc.name && (
                    <Text type="secondary" style={{ marginLeft: 8 }}>
                        — {acc.name}
                    </Text>
                )}
            </div>
            <Text type="secondary" style={{ fontSize: 11 }}>
                {financialGroup}
            </Text>

            <Divider style={{ margin: '8px 0' }} />

            {/* Behavior */}
            <SectionHeader>Behavior</SectionHeader>
            <Row
                label="Metric"
                value={formatMetric(acc)}
                muted={isDefault.metric(acc)}
            />
            <Row
                label="Sum (periods)"
                value={formatSumType(acc)}
                muted={isDefault.sumType(acc)}
            />
            <Row
                label="BC combine"
                value={formatBcCombine(acc)}
                muted={isDefault.bcCombine(acc)}
            />
            <Row
                label="Roll up"
                value={formatRollUp(acc)}
                muted={isDefault.rollUp(acc)}
            />

            <Divider style={{ margin: '8px 0' }} />

            {/* Flags */}
            <SectionHeader>Flags</SectionHeader>
            <Row
                label="Carry"
                value={formatYesNo(acc.carry)}
                muted={isDefault.carry(acc)}
            />
            <Row
                label="Convergence"
                value={formatYesNo(acc.convergence)}
                muted={isDefault.convergence(acc)}
            />
            <Row
                label="Exclude"
                value={formatYesNo(acc.exclude)}
                muted={isDefault.exclude(acc)}
            />
            {acc.is_change_in && (
                <Row label="Change-in" value="Yes" muted={false} />
            )}
            {acc.cash_effect !== undefined && acc.cash_effect !== null && (
                <Row
                    label="Cash effect"
                    value={formatCashEffect(acc)}
                    muted={false}
                />
            )}

            {/* Formulas */}
            {totalPeriods > 0 && (
                <>
                    <Divider style={{ margin: '8px 0' }} />
                    <SectionHeader>Formulas</SectionHeader>
                    <Row
                        label="Coverage"
                        value={`${formulaCount} of ${totalPeriods} periods`}
                        muted={formulaCount === 0}
                    />
                </>
            )}
        </div>
    );
};

export default AccountConfigSummary;